import { useCallback, useEffect, useRef, useState } from "react";
import { buscarEnderecoPorCep } from "./buscaCep";
import { somenteDigitos } from "./camposAtualizacaoCadastral";

export type SituacaoCep = "buscando" | "naoEncontrado" | "indisponivel" | null;

export const MENSAGEM_SITUACAO_CEP: { [situacao: string]: string } = {
  buscando: "Buscando endereço...",
  naoEncontrado: "CEP não encontrado. Preencha o endereço.",
  indisponivel: "Não foi possível buscar o endereço. Preencha manualmente.",
};

/** Campos do formulario preenchidos pelo CEP; os vazios no ViaCEP (CEP geral da cidade) ficam como estao. */
export type EnderecoPreenchido = { ENDERECO?: string; BAIRRO?: string; CIDADE?: string };

/** Ao completar os 8 digitos do CEP, busca o endereco e entrega os campos encontrados. */
const usePreenchimentoCep = (preencher: (endereco: EnderecoPreenchido) => void) => {
  const [situacao, setSituacao] = useState<SituacaoCep>(null);
  // CEP da busca em andamento: resposta de um CEP que o usuario ja trocou e descartada.
  const cepAtual = useRef("");
  const montado = useRef(true);
  useEffect(
    () => () => {
      montado.current = false;
    },
    []
  );

  const aoAlterarCep = useCallback(
    async (cep: string) => {
      const digitos = somenteDigitos(cep);
      if (digitos === cepAtual.current) {
        return;
      }
      cepAtual.current = digitos;
      if (digitos.length !== 8) {
        setSituacao(null);
        return;
      }
      setSituacao("buscando");
      let novaSituacao: SituacaoCep = null;
      try {
        const endereco = await buscarEnderecoPorCep(digitos);
        if (!montado.current || cepAtual.current !== digitos) {
          return;
        }
        if (endereco) {
          preencher({
            ENDERECO: endereco.logradouro || undefined,
            BAIRRO: endereco.bairro || undefined,
            CIDADE: endereco.cidade && endereco.uf ? `${endereco.cidade} - ${endereco.uf}` : undefined,
          });
        } else {
          novaSituacao = "naoEncontrado";
        }
      } catch (e) {
        if (!montado.current || cepAtual.current !== digitos) {
          return;
        }
        novaSituacao = "indisponivel";
      }
      setSituacao(novaSituacao);
    },
    [preencher]
  );

  return { situacaoCep: situacao, aoAlterarCep };
};

export default usePreenchimentoCep;
